import { useMemo } from 'react';
import { useGetSquaresQuery, type SquaresContestResponse } from '../api/squares/getSquares';
import { useGetMyPicksQuery } from '../api/squares/getMyPicks';

export type AppEntryResult =
  | { showRulesView: true; mostRecentPendingContestId: string | null }
  | {
      showRulesView: false;
      redirectTo: { path: '/play'; contestId: string; mode?: 'results' } | { path: '/results' };
    };

/**
 * App entry navigation logic:
 *
 * 1. No active and no pending contests → ResultsView
 *
 * 2. Pending contest user has NOT entered → show RulesView (Play Now → that pending)
 *
 * 3. User has entry in pending or active AND no other pending to enter → bypass RulesView,
 *    go straight to PlayView for that contest
 */
export function useAppEntryRedirect(options?: { enabled?: boolean }): {
  result: AppEntryResult | null;
  isLoading: boolean;
  error: unknown;
} {
  const enabled = options?.enabled !== false;
  const { data: myPicks = [], isLoading: myPicksLoading } = useGetMyPicksQuery({ enabled });
  const {
    data: pendingContests = [],
    isLoading: pendingLoading,
    error: pendingError,
  } = useGetSquaresQuery({ status: 'PENDING', limit: 100 }, { enabled });
  const {
    data: activeContests = [],
    isLoading: activeLoading,
    error: activeError,
  } = useGetSquaresQuery({ status: 'ACTIVE', limit: 100 }, { enabled });

  const isLoading = myPicksLoading || pendingLoading || activeLoading;
  const error = pendingError || activeError;

  const result = useMemo((): AppEntryResult | null => {
    if (isLoading) return null;

    const pending = pendingContests as SquaresContestResponse[];
    const active = activeContests as SquaresContestResponse[];
    const now = Date.now();

    const enteredContestIds = new Set<string>();
    myPicks.forEach(
      (pick: { question_id?: string; squares_contest_id?: string; pickem_id?: string }) => {
        if (pick.question_id) enteredContestIds.add(String(pick.question_id));
        if (pick.squares_contest_id) enteredContestIds.add(String(pick.squares_contest_id));
        if (pick.pickem_id) enteredContestIds.add(String(pick.pickem_id));
      }
    );

    const isWithinOpenWindow = (c: SquaresContestResponse) => {
      if (c.status?.toUpperCase() !== 'PENDING') return false;
      const openTime = c.selection_editable_until_utc || c.open_time_utc;
      const startTime = c.start_time_utc;
      const openMs = openTime ? new Date(openTime).getTime() : null;
      const startMs = startTime ? new Date(startTime).getTime() : null;
      if (openMs != null && now < openMs) return false;
      if (startMs != null && now >= startMs) return false;
      return true;
    };

    const pendingWithinWindow = pending.filter(isWithinOpenWindow);

    const pendingAvailable = pendingWithinWindow.filter(c => {
      const id = c.squares_contest_id || c.question_id;
      return id && !enteredContestIds.has(String(id));
    });

    const sortByStartTime = (a: SquaresContestResponse, b: SquaresContestResponse) => {
      const ta = a.start_time_utc ? new Date(a.start_time_utc).getTime() : 0;
      const tb = b.start_time_utc ? new Date(b.start_time_utc).getTime() : 0;
      return ta - tb;
    };

    // Most upcoming contest (pending or active) - for when user has entered all pending
    const allContestsByTime = [...pendingWithinWindow, ...active].sort(sortByStartTime);
    const mostUpcomingContest = allContestsByTime[0];
    const mostUpcomingId = mostUpcomingContest
      ? mostUpcomingContest.squares_contest_id || mostUpcomingContest.question_id
      : null;

    // 1. No active and no pending → ResultsView
    if (pendingWithinWindow.length === 0 && active.length === 0) {
      return { showRulesView: false, redirectTo: { path: '/results' } };
    }

    // 2. Pending contest user has NOT entered → show RulesView
    // TEMPORARILY BYPASSED: go straight to PlayView instead of RulesView
    // if (pendingAvailable.length > 0) {
    //   const firstPending = [...pendingAvailable].sort(sortByStartTime)[0];
    //   const contestId = firstPending?.squares_contest_id || firstPending?.question_id || null;
    //   return { showRulesView: true, mostRecentPendingContestId: contestId };
    // }
    if (pendingAvailable.length > 0) {
      const firstPending = [...pendingAvailable].sort(sortByStartTime)[0];
      const contestId = firstPending?.squares_contest_id || firstPending?.question_id;
      if (contestId) {
        return {
          showRulesView: false,
          redirectTo: { path: '/play', contestId },
        };
      }
    }

    // 3. User has entry in pending or active, no other pending to enter → bypass RulesView
    // When contest is ACTIVE or COMPLETE, entries are locked — redirect with mode=results to show View Results CTA
    if (mostUpcomingId) {
      const status = (mostUpcomingContest?.status ?? '').toUpperCase();
      const entriesLocked = status === 'ACTIVE' || status === 'COMPLETE';
      return {
        showRulesView: false,
        redirectTo: {
          path: '/play',
          contestId: mostUpcomingId,
          ...(entriesLocked && { mode: 'results' as const }),
        },
      };
    }

    return { showRulesView: false, redirectTo: { path: '/results' } };
  }, [myPicks, pendingContests, activeContests, isLoading]);

  return { result, isLoading, error };
}
